<template>
  <NuxtLink
    class="direction"
    :class="ui?.direction"
    :to="`/directions/${direction.id}`"
    :style="{
      '--card-color': getCardColor(direction.slug),
    }"
  >
    <slot name="name">
      <span v-if="!noTitle" class="direction-name" :class="ui?.name">{{
        direction?.name
      }}</span>
    </slot>

    <div v-if="figure" class="figure" />

    <img
      :src="getDirectionIllustration(direction?.name)"
      class="image"
      :class="ui?.image"
    />
  </NuxtLink>
</template>

<script lang="ts" setup>
import type { Direction } from '~/api'
import { getDirectionIllustration, SLUG_CARD_COLORS, type Slug } from '~/shared'

type TDirection = Partial<
  Direction & {
    slug?: Slug
  }
>

defineOptions({
  name: 'SPDirection',
})

withDefaults(
  defineProps<{
    direction: TDirection
    noTitle?: boolean
    figure?: boolean
    ui?: {
      direction?: string
      name?: string
      image?: string
    }
  }>(),
  {
    figure: true,
    noTitle: false,
  },
)

const getCardColor = (slug: Slug): string =>
  SLUG_CARD_COLORS?.[slug as keyof typeof SLUG_CARD_COLORS] ||
  SLUG_CARD_COLORS.default
</script>

<style lang="scss" scoped>
.direction {
  position: relative;
  display: flex;
  flex-grow: 1;
  flex-shrink: 0;
  align-items: flex-end;
  justify-content: center;
  padding: 12px 24px;
  height: 168px;
  background-color: var(--color-white);
  border-radius: 16px;
  width: calc(100% / var(--columns) - var(--directions-gap));
  overflow: hidden;

  .figure {
    width: 80px;
    height: 80px;
    position: absolute;
    top: 16px;
    left: 50%;
    transform: translate(-50%, 14px) rotate(-45deg);
    background: var(--card-color);
    border-radius: 12px;
    z-index: 0;

    @include isLessThan('xl') {
      top: unset;
      left: unset;
      transform: rotate(-45deg);
      right: -15px;
      bottom: -20px;
      border-radius: 12px;
      width: 60px;
      height: 60px;
    }
  }

  &.direction--all {
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  @include isBigTablet {
    height: 100px;
  }

  @include isLessThan('xl') {
    align-items: flex-start;
    justify-content: flex-start;
    height: 100px;
    padding: 12px 16px;
  }
}

.direction-name {
  font-size: var(--text-size-h6);
  font-weight: 700;
  color: var(--color-neutral-default);
  z-index: 1;
}

.image {
  position: absolute;
  top: 15%;
  left: 40%;
  height: 50%;
  z-index: 2;

  @include isLessThan('xl') {
    top: unset;
    bottom: 5px;
    left: unset;
    right: 5%;
    height: 70%;
    width: auto;
    height: 60px;
  }
}
</style>
